import { createAsyncThunk } from '@reduxjs/toolkit';

import { driverAPI } from '@/modules/driver/api';
import { TUpdateOwnDriverInfoForm } from '@/modules/driver/model/driver';
import { namespace } from '@/modules/driver/namespace';
import { getMyDriver } from '@/modules/driver/store/selectors';
import type { RootState } from '@/root-store';
import { ThunkResult } from '@/shared/actions/thunk-result';
import { ISubmitFormResult } from '@/shared/models/formSubmit';

import { updateMyDriver } from '../actions';

let lastRequestId: string | undefined;

const updateOwnDriverInfo = createAsyncThunk<
  ThunkResult<ISubmitFormResult<TUpdateOwnDriverInfoForm>>,
  Partial<TUpdateOwnDriverInfoForm>
>(
  `${namespace}/submit-update-own-driver-info`,
  async (formValues, { dispatch, requestId, getState }) => {
    try {
      if (lastRequestId !== undefined) {
        return { isAlreadyExecuted: true };
      }

      lastRequestId = requestId;

      const result: ISubmitFormResult<TUpdateOwnDriverInfoForm> = {};

      const driver = getMyDriver(getState() as RootState);

      const [, driverUpdateErrors] = await driverAPI.updateDriver(driver.id, {
        ...driver,
        ...formValues,
      });

      if (driverUpdateErrors) {
        const { licensingAuthority, licenseNumber, homeAddress, ...other } = driverUpdateErrors;

        if (licensingAuthority || licenseNumber || homeAddress) {
          result.validationErrors = {
            licensingAuthority,
            licenseNumber,
            homeAddress,
          };
        }

        result.baseErrors = Object.values(other).flat();
      }

      if (!driverUpdateErrors) {
        await dispatch(updateMyDriver(formValues));
      }

      return {
        isSucceed: true,
        result,
      };
    } finally {
      lastRequestId = undefined;
    }
  },
);

export { updateOwnDriverInfo };
